/**
 * <embla-carousel> – Web component for Embla Carousel.
 * Requires: EmblaCarousel (embla-carousel.js), optional EmblaCarouselAutoplay, EmblaCarouselAutoScroll (from assets).
 * Markup: .embla__viewport > .embla__container > .embla__slide children.
 * Data attributes:
 *   data-embla-mobile-enabled, data-embla-tablet-enabled, data-embla-desktop-enabled
 *   data-embla-ipr-mobile, data-embla-ipr-tablet, data-embla-ipr-desktop (slides visible per row, CSS only; arrows scroll 1 slide)
 *   data-embla-arrows="true"
 *   data-embla-autoplay, data-embla-autoplay-delay (ms)
 *   data-embla-auto-scroll="true" – continuous smooth scroll (Auto Scroll plugin), overrides autoplay
 *   data-embla-auto-scroll-speed (pixels per frame, decimals allowed e.g. 0.4), data-embla-auto-scroll-direction, data-embla-auto-scroll-start-delay
 *   The autoplay / auto scroll attributes above configure desktop. Add -mobile or -tablet to override them per
 *   breakpoint (e.g. data-embla-autoplay-mobile, data-embla-auto-scroll-speed-tablet); missing or empty overrides fall back
 *   to desktop, except auto scroll itself, which stays off unless that breakpoint sets it to "true".
 *   data-embla-init-scroll-to (id to scroll to on init)
 *   data-embla-loop-clones="true" – duplicate slides when there are too few for Embla to loop
 * CSS: --embla-align-inset on the viewport sets the snap start (px from the viewport start).
 */
(function () {
  if (typeof EmblaCarousel === 'undefined') return;

  // Auto-advancing is motion; visitors who prefer reduced motion should not get it started.
  // Manual navigation (arrows/dots/swipe) still works because the plugins stay registered.
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const MEDIA = {
    mobile: '(max-width: 767px)',
    tablet: '(min-width: 768px) and (max-width: 1023px)',
    desktop: '(min-width: 1024px)'
  };

  const readMotion = (dataset, suffix = '') => {
    const read = (key) => dataset[key + suffix] || dataset[key];
    const autoScroll = dataset['emblaAutoScroll' + suffix] === 'true';
    /* parseFloat, not parseInt: the plugin moves `speed` pixels per frame, so decimals below 1
       are the only way to scroll slower than 1px/frame. parseInt would floor 0.4 to 0. */
    const autoScrollSpeed = parseFloat(read('emblaAutoScrollSpeed'));
    return {
      autoScroll,
      autoScrollSpeed: autoScrollSpeed > 0 ? autoScrollSpeed : 1,
      autoplay: !autoScroll && read('emblaAutoplay') === 'true',
      autoplayDelay: parseInt(read('emblaAutoplayDelay'), 10) || 5000
    };
  };

  /* Desktop is the base so widths between the breakpoint queries behave like desktop.
     Embla watches plugin breakpoints and re-inits the plugins when they change. */
  const motionOptions = (motion, toOptions) => ({
    ...toOptions(motion.desktop),
    breakpoints: {
      [MEDIA.mobile]: toOptions(motion.mobile),
      [MEDIA.tablet]: toOptions(motion.tablet)
    }
  });

  /* Mirrors how Embla resolves a plugin's `active` option for the current media. */
  const isActiveAtMedia = ({ active, breakpoints = {} }) => Object.entries(breakpoints)
    .filter(([query]) => window.matchMedia(query).matches)
    .reduce((isActive, [, options]) => options.active ?? isActive, active !== false);

  /* A track that fits needs no scrolling, so Embla trims it to a single snap. */
  const canScroll = (emblaApi) => emblaApi.scrollSnapList().length > 1;
  const isDraggable = (emblaApi) => emblaApi.internalEngine().options.active && canScroll(emblaApi);

  /* Embla only loops when it can, otherwise it silently falls back to loop: false. */
  const MAX_LOOP_CLONE_SETS = 3;
  const FOCUSABLE = 'a[href], button, input, select, textarea, summary, iframe, [tabindex], [contenteditable]';
  const needsLoopClones = (emblaApi) => {
    const { options } = emblaApi.internalEngine();
    return options.active && !options.loop && canScroll(emblaApi);
  };
  const cloneSlide = (slide) => {
    const clone = slide.cloneNode(true);
    clone.dataset.emblaClone = '';
    clone.classList.remove('active');
    clone.removeAttribute('id');
    clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
    /* Not inert: clones must stay clickable, only hidden from keyboard and assistive tech. */
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll(FOCUSABLE).forEach((node) => node.setAttribute('tabindex', '-1'));
    return clone;
  };
  const removeLoopClones = (container) => {
    const clones = container.querySelectorAll(':scope > [data-embla-clone]');
    clones.forEach((clone) => clone.remove());
    return clones.length > 0;
  };

  if (!customElements.get('embla-carousel')) {
    customElements.define('embla-carousel', class EmblaCarouselElement extends HTMLElement {
      #emblaResizeObserver;
      #updateArrowsVisibility;

      connectedCallback() {
        if (!this.classList.contains('embla')) this.classList.add('embla');
        this.init();
      }

      init() {
        if (this.dataset.emblaInitialized === 'true') return;
        const viewport = this.querySelector('.embla__viewport');
        if (!viewport) return;

        const mobileEnabled = this.dataset.emblaMobileEnabled === 'true';
        const tabletEnabled = this.dataset.emblaTabletEnabled === 'true';
        const desktopEnabled = this.dataset.emblaDesktopEnabled === 'true';
        const sliderEnabledAnywhere = mobileEnabled || tabletEnabled || desktopEnabled;

        if (!sliderEnabledAnywhere) {
          this.dataset.emblaGridOnly = 'true';
          this.dataset.emblaInitialized = 'true';
          return;
        }

        const arrows = this.dataset.emblaArrows === 'true';
        const motion = {
          mobile: readMotion(this.dataset, 'Mobile'),
          tablet: readMotion(this.dataset, 'Tablet'),
          desktop: readMotion(this.dataset)
        };
        const motionAnywhere = (type) => Object.values(motion).some((settings) => settings[type]);
        const autoScrollDirection = (this.dataset.emblaAutoScrollDirection || 'forward').toLowerCase();
        const autoScrollStartDelay = parseInt(this.dataset.emblaAutoScrollStartDelay, 10) || 1000;
        const initScrollTo = this.dataset.emblaInitScrollTo;

        /* Edge-to-edge card tracks stay full-bleed while dragging. `align` is Embla's
           snap inset, read from CSS so each breakpoint decides its own left gap. */
        const alignInset = () => parseFloat(getComputedStyle(viewport).getPropertyValue('--embla-align-inset')) || 0;
        const loopClones = this.dataset.emblaLoopClones === 'true';

        const options = {
          align: alignInset,
          loop: true,
          skipSnaps: true,
          slidesToScroll: 1,
          watchDrag: canScroll,
          duration: 32,
          /* Honor reduced motion: instant (non-sliding) slide changes for manual/programmatic nav. */
          breakpoints: {
            '(prefers-reduced-motion: reduce)': { duration: 0 }
          }
        };

        /* Breakpoints set to grid deactivate Embla there so the container renders as a static CSS grid
           instead of a transformed slider (supports mixed grid/slider configs across breakpoints). */
        if (!mobileEnabled) options.breakpoints[MEDIA.mobile] = { active: false };
        if (!tabletEnabled) options.breakpoints[MEDIA.tablet] = { active: false };
        if (!desktopEnabled) options.breakpoints[MEDIA.desktop] = { active: false };

        const plugins = [];
        if (motionAnywhere('autoScroll') && typeof EmblaCarouselAutoScroll !== 'undefined') {
          plugins.push(EmblaCarouselAutoScroll({
            ...motionOptions(motion, (settings) => ({ active: settings.autoScroll, speed: settings.autoScrollSpeed })),
            startDelay: autoScrollStartDelay,
            direction: autoScrollDirection === 'backward' ? 'backward' : 'forward',
            stopOnInteraction: true,
            stopOnMouseEnter: false
          }));
        }
        if (motionAnywhere('autoplay') && typeof EmblaCarouselAutoplay !== 'undefined') {
          plugins.push(EmblaCarouselAutoplay({
            ...motionOptions(motion, (settings) => ({ active: settings.autoplay, delay: settings.autoplayDelay })),
            stopOnInteraction: true
          }));
        }

        const emblaApi = EmblaCarousel(viewport, options, plugins);
        this.emblaApi = emblaApi;
        this.dataset.emblaInitialized = 'true';

        /* Embla re-inits on resize, breakpoint changes and loop clone updates. */
        const syncDraggable = () => this.toggleAttribute('data-embla-draggable', isDraggable(emblaApi));
        syncDraggable();
        emblaApi.on('reInit', syncDraggable);

        /* Whether clones are needed depends on the viewport width, so re-evaluate from scratch.
           Height-only resizes (e.g. lazy images) are skipped so a running track does not jump. */
        let loopClonesWidth;
        const syncLoopClones = () => {
          if (!loopClones || viewport.offsetWidth === loopClonesWidth) return;
          loopClonesWidth = viewport.offsetWidth;
          const slideContainer = emblaApi.containerNode();
          if (removeLoopClones(slideContainer)) emblaApi.reInit();
          const originals = emblaApi.slideNodes();

          for (let sets = 0; sets < MAX_LOOP_CLONE_SETS && needsLoopClones(emblaApi); sets++) {
            slideContainer.append(...originals.map(cloneSlide));
            emblaApi.reInit();
          }

          const { options: engineOptions } = emblaApi.internalEngine();
          this.toggleAttribute('data-embla-static', engineOptions.active && !engineOptions.loop);
        };
        syncLoopClones();

        const resizeObserver = new ResizeObserver(() => {
          if (this.dataset.emblaInitialized !== 'true' || !this.emblaApi) return;
          emblaApi.reInit();
          syncLoopClones();
          this.#updateArrowsVisibility?.();
          const autoPlugin = this.activeAutoPlugin();
          if (autoPlugin) {
            const pausedByOverlay = Marshall?.Media?.isCarouselPausedByOverlay?.(this);

            if (!prefersReducedMotion() && !pausedByOverlay) {
              autoPlugin.plugin.play();
            } else {
              autoPlugin.plugin.stop();
            }
          }
        });
        resizeObserver.observe(viewport);
        this.#emblaResizeObserver = resizeObserver;

        if (arrows) {
          const prevBtn = this.querySelector('[data-embla-prev]');
          const nextBtn = this.querySelector('[data-embla-next]');
          const arrowsContainer = prevBtn && prevBtn.parentElement;
          const updateArrowsVisibility = () => {
            /* Nothing to scroll to: hide the arrows even for auto-scroll / autoplay sliders. */
            if (!canScroll(emblaApi)) {
              if (arrowsContainer) arrowsContainer.style.display = 'none';
              return;
            }

            if (arrowsContainer) arrowsContainer.style.display = '';

            if (this.activeAutoPlugin()?.type === 'autoScroll') {
              if (prevBtn) prevBtn.style.display = '';
              if (nextBtn) nextBtn.style.display = '';
              return;
            }

            const canPrev = emblaApi.canScrollPrev();
            const canNext = emblaApi.canScrollNext();
            if (prevBtn) prevBtn.style.display = canPrev ? '' : 'none';
            if (nextBtn) nextBtn.style.display = canNext ? '' : 'none';
          };
          
          this.#updateArrowsVisibility = updateArrowsVisibility;
          emblaApi.on('select', updateArrowsVisibility);
          emblaApi.on('resize', updateArrowsVisibility);
          const stopAutoAndScroll = (scrollFn) => {
            const activePlugins = emblaApi.plugins() || {};
            if (activePlugins.autoScroll && typeof activePlugins.autoScroll.stop === 'function') {
              activePlugins.autoScroll.stop();
            }
            if (activePlugins.autoplay && typeof activePlugins.autoplay.stop === 'function') {
              activePlugins.autoplay.stop();
            }
            scrollFn();
          };
          if (prevBtn) prevBtn.addEventListener('click', () => stopAutoAndScroll(() => emblaApi.scrollPrev()));
          if (nextBtn) nextBtn.addEventListener('click', () => stopAutoAndScroll(() => emblaApi.scrollNext()));
          updateArrowsVisibility();
        }

        if (initScrollTo) {
          const el = document.getElementById(initScrollTo);
          if (el) {
            const slides = this.querySelectorAll('.embla__slide');
            const idx = Array.from(slides).indexOf(el.closest('.embla__slide') || el);
            if (idx >= 0) emblaApi.scrollTo(idx, true);
          }
        }

        emblaApi.on('select', () => {
          const slides = this.querySelectorAll('.embla__slide');
          slides.forEach((s, i) => {
            const isSelected = emblaApi.selectedScrollSnap() === i;
            s.classList.toggle('active', isSelected);
          });

          const indicator = this.querySelector('.embla-indicator');
          if (indicator) {
            indicator.querySelectorAll('[data-embla-dot-index]').forEach((dot) => {
              const idx = parseInt(dot.dataset.emblaDotIndex, 10);
              dot.classList.toggle('active', !isNaN(idx) && emblaApi.selectedScrollSnap() === idx);
            });
          }
        });

        if (this.dataset.emblaIndicator === 'true') {
          this.querySelectorAll('.embla-indicator [data-embla-dot-index]').forEach((dot) => {
            dot.addEventListener('click', () => {
              const activePlugins = emblaApi.plugins() || {};
              if (activePlugins.autoScroll && typeof activePlugins.autoScroll.stop === 'function') {
                activePlugins.autoScroll.stop();
              }
              if (activePlugins.autoplay && typeof activePlugins.autoplay.stop === 'function') {
                activePlugins.autoplay.stop();
              }
              const idx = parseInt(dot.dataset.emblaDotIndex, 10);
              if (!isNaN(idx)) emblaApi.scrollTo(idx, true);
            });
          });
        }

        const autoPlugin = this.activeAutoPlugin();
        if (autoPlugin && !prefersReducedMotion()) autoPlugin.plugin.play();

        const firstSnap = emblaApi.selectedScrollSnap();
        this.querySelectorAll('.embla__slide').forEach((s, i) => {
          s.classList.toggle('active', firstSnap === i);
        });
        const indicator = this.querySelector('.embla-indicator');
        if (indicator) {
          indicator.querySelectorAll('[data-embla-dot-index]').forEach((dot) => {
            const idx = parseInt(dot.dataset.emblaDotIndex, 10);
            dot.classList.toggle('active', !isNaN(idx) && firstSnap === idx);
          });
        }
      }

      /* At most one of autoplay / auto scroll is active per breakpoint. Plugins that are inactive here
         were never initialised (or were destroyed), so only this one is safe to play(). */
      activeAutoPlugin() {
        const emblaApi = this.emblaApi;
        if (!emblaApi || !emblaApi.internalEngine().options.active || !canScroll(emblaApi)) return null;

        const plugins = emblaApi.plugins() || {};
        const type = ['autoScroll', 'autoplay'].find((name) => plugins[name] && isActiveAtMedia(plugins[name].options));
        return type ? { plugin: plugins[type], type } : null;
      }

      disconnectedCallback() {
        /* Always disconnect: if the viewport was removed too, a queued callback could still fire against a destroyed api. */
        if (this.#emblaResizeObserver) {
          this.#emblaResizeObserver.disconnect();
          this.#emblaResizeObserver = undefined;
        }
        if (this.emblaApi) {
          this.emblaApi.destroy();
          this.emblaApi = undefined;
        }
        /* Clones must not be counted as original slides if the element is reconnected. */
        const container = this.querySelector('.embla__container');
        if (container) removeLoopClones(container);
        this.#updateArrowsVisibility = undefined;
        this.dataset.emblaInitialized = '';
        delete this.dataset.emblaGridOnly;
        this.removeAttribute('data-embla-static');
        this.removeAttribute('data-embla-draggable');
      }
    });
  }

  window.initEmblaCarousel = function () {
    document.querySelectorAll('embla-carousel[data-embla]').forEach((el) => {
      if (el.init) el.init();
    });
  };
})();
